import { Header } from "@/components/Header";
import { BusinessDetailsDialog } from "@/components/onboarding/BusinessDetailsDialog";
import { SettingsDialog } from "@/components/onboarding/SettingsDialog";
import { ServiceCreatedDialog } from "@/components/onboarding/ServiceCreatedDialog";
import { AddServicesDialog } from "@/components/onboarding/AddServicesDialog";
import { ProfileImageCropper } from "@/components/onboarding/ProfileImageCropper";
import { BackButton } from "@/components/onboarding/BackButton";
import { useOnboarding } from "@/hooks/useOnboarding";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { signOut } from "@/lib/auth";

const Onboarding = () => {
  const { toast } = useToast();
  const {
    currentStep,
    isLoading,
    businessDetails,
    profileImage,
    tempImageUrl,
    showCropDialog,
    isGeneratingName,
    businessName,
    setShowCropDialog,
    setTempImageUrl,
    setBusinessName,
    handleBusinessDetailsSubmit,
    handleSettingsSubmit,
    handleImageUpload,
    handleCropComplete,
    generateBusinessName,
    handleBackStep,
    navigate
  } = useOnboarding();

  // Handler for forcing exit from onboarding
  const handleForceExit = async () => {
    try {
      // Try to sign out properly
      await signOut();
      
      // Redirect to the landing page
      navigate('/', { replace: true });
      
      toast({
        title: "Exited onboarding",
        description: "You have been signed out and can try again later.",
      });
    } catch (error) {
      console.error('Error during force exit:', error);
      // Force redirect even if signout fails
      window.location.href = '/';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      <Header />
      {currentStep !== 'businessDetails' && (
        <BackButton className="md:fixed" onClick={handleBackStep} />
      )}
      
      {/* Emergency exit button */}
      <Button
        variant="destructive"
        size="sm"
        className="absolute top-6 right-6 z-50"
        onClick={handleForceExit}
      >
        <X className="h-4 w-4 mr-1" />
        Exit
      </Button>
      
      <div className="container mx-auto px-4 py-24 flex flex-col items-center justify-center">
        <img 
          src="/lovable-uploads/bc4b57d4-e29b-4e44-8e1c-82ec09ca6fd6.png" 
          alt="Gebeya" 
          className="h-12 mb-8" 
        />
        
        <BusinessDetailsDialog
          isOpen={currentStep === 'businessDetails'}
          isLoading={isLoading}
          onSubmit={handleBusinessDetailsSubmit}
        />

        <SettingsDialog
          isOpen={currentStep === 'settings'}
          isLoading={isLoading}
          businessName={businessName}
          profileImage={profileImage}
          isGeneratingName={isGeneratingName}
          onSubmit={handleSettingsSubmit}
          onBusinessNameChange={setBusinessName}
          onGenerateBusinessName={generateBusinessName}
          onImageUpload={handleImageUpload}
          country={businessDetails.country}
        />

        <ServiceCreatedDialog
          isOpen={currentStep === 'serviceCreated'}
          onNext={() => navigate('/add-services')}
        />

        <AddServicesDialog
          isOpen={currentStep === 'addServices'}
          onNavigateToServices={() => navigate('/add-services')}
        />

        <ProfileImageCropper
          open={showCropDialog}
          onClose={() => {
            setShowCropDialog(false);
            setTempImageUrl(null);
          }}
          imageUrl={tempImageUrl || ''}
          onCropComplete={handleCropComplete}
        />
      </div>
    </div>
  );
};

export default Onboarding;
